import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle2, ArrowRight, ShieldCheck, Flame, Layers, HelpCircle, Package, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EnteringLevelStudio() {
  const [stats, setStats] = useState(null);
  const [grade, setGrade] = useState('K');
  const [modality, setModality] = useState('reading');
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regenId, setRegenId] = useState(null);
  const [parity, setParity] = useState(null);
  const [screenerParity, setScreenerParity] = useState(null);
  const [prewarming, setPrewarming] = useState(null); // 'entering' | 'standard' | 'screener' | null
  const [kdpBusy, setKdpBusy] = useState(false);
  const [kdpTier, setKdpTier] = useState('ENTERING');
  const [kdpGrade, setKdpGrade] = useState('');
  const [pcusaBusy, setPcusaBusy] = useState(false);
  const [pcusaTier, setPcusaTier] = useState('ENTERING');
  const [pcusaGrade, setPcusaGrade] = useState('');
  const [pcusaQty, setPcusaQty] = useState('100');

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/entering-level/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) { toast.error('Failed to load stats'); }
  }, []);

  const loadParity = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/edition-parity/audit?grades=K,1,2`, { withCredentials: true });
      setParity(data);
    } catch (e) { /* silent */ }
    try {
      const { data } = await axios.get(`${API}/api/edition-parity/screener-audit`, { withCredentials: true });
      setScreenerParity(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/entering-level/preview`, {
        params: { grade, modality, limit: 30, only_level12: true },
        withCredentials: true,
      });
      setPreview(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    setLoading(false);
  }, [grade, modality]);

  useEffect(() => { loadStats(); loadParity(); }, [loadStats, loadParity]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const prewarm = async (kind) => {
    setPrewarming(kind);
    const path = `/api/edition-parity/prewarm-${kind}`;
    const label = { entering: '24 Entering', standard: '72 Standard', screener: '10 Screener' }[kind];
    toast.info(`Pre-warming ${label} PDFs — this may take up to 15 minutes for the summative tiers.`);
    try {
      const { data } = await axios.post(`${API}${path}`, {}, {
        withCredentials: true,
        timeout: 1800000,
      });
      toast.success(`Pre-warm complete: ${data.summary.generated} generated, ${data.summary.already_cached} already cached, ${data.summary.errors} errors`);
      await loadParity();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Pre-warm ${kind} failed`);
    }
    setPrewarming(null);
  };

  const downloadKdpBundle = async () => {
    setKdpBusy(true);
    toast.info(`Building Amazon KDP bundle (tier=${kdpTier}${kdpGrade ? ', grade=' + kdpGrade : ''}). This may take up to a minute for larger tiers.`);
    try {
      const params = new URLSearchParams({ filter_tier: kdpTier });
      if (kdpGrade) params.set('filter_grade', kdpGrade);
      const res = await axios.post(`${API}/api/kdp-export/bundle?${params.toString()}`, {}, {
        responseType: 'blob',
        withCredentials: true,
        timeout: 300000,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mosaic-kdp-bundle-${kdpTier}${kdpGrade ? '-G' + kdpGrade : ''}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('KDP bundle downloaded — unzip and follow the README to upload to Amazon KDP');
    } catch (e) {
      let detail = 'KDP bundle build failed';
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          detail = j.detail || txt.slice(0, 200);
        } catch { /* fall through */ }
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(detail);
    }
    setKdpBusy(false);
  };

  const downloadKdpBinder = async () => {
    setKdpBusy(true);
    toast.info(`Building desk-binder PDF (tier=${kdpTier}${kdpGrade ? ', grade=' + kdpGrade : ''}).`);
    try {
      const params = new URLSearchParams({ filter_tier: kdpTier });
      if (kdpGrade) params.set('filter_grade', kdpGrade);
      const res = await axios.get(`${API}/api/kdp-export/binder.pdf?${params.toString()}`, {
        responseType: 'blob',
        withCredentials: true,
        timeout: 300000,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mosaic-kdp-desk-binder-${kdpTier}${kdpGrade ? '-G' + kdpGrade : ''}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Desk-binder PDF downloaded — print & three-hole punch');
    } catch (e) {
      // Axios returns Blob on error when responseType=blob — parse the JSON out of it
      let detail = 'Binder build failed';
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          detail = j.detail || txt.slice(0, 200);
        } catch { /* fall through */ }
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(detail);
    }
    setKdpBusy(false);
  };

  const downloadPcusaOrderSheets = async () => {
    const qty = parseInt(pcusaQty, 10);
    if (!qty || qty < 10) {
      toast.error('Quantity must be at least 10 (PCUSA minimum order)');
      return;
    }
    setPcusaBusy(true);
    toast.info(`Building PCUSA order sheets (tier=${pcusaTier}${pcusaGrade ? ', grade=' + pcusaGrade : ''}, qty=${qty} per booklet).`);
    try {
      const params = new URLSearchParams({ filter_tier: pcusaTier, quantity: String(qty) });
      if (pcusaGrade) params.set('filter_grade', pcusaGrade);
      const res = await axios.get(`${API}/api/pcusa-export/order-sheets.pdf?${params.toString()}`, {
        responseType: 'blob',
        withCredentials: true,
        timeout: 300000,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mosaic-pcusa-order-sheets-${pcusaTier}${pcusaGrade ? '-G' + pcusaGrade : ''}-qty${qty}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PCUSA order sheets downloaded — print, three-hole punch, and walk through the online quote tool');
    } catch (e) {
      let detail = 'PCUSA order sheets build failed';
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          detail = j.detail || txt.slice(0, 200);
        } catch { /* fall through */ }
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(detail);
    }
    setPcusaBusy(false);
  };

  const downloadPcusaBundle = async () => {
    const qty = parseInt(pcusaQty, 10);
    if (!qty || qty < 10) {
      toast.error('Quantity must be at least 10 (PCUSA minimum order)');
      return;
    }
    setPcusaBusy(true);
    toast.info(`Building PCUSA upload bundle (tier=${pcusaTier}${pcusaGrade ? ', grade=' + pcusaGrade : ''}). May take up to a minute.`);
    try {
      const params = new URLSearchParams({ filter_tier: pcusaTier, quantity: String(qty) });
      if (pcusaGrade) params.set('filter_grade', pcusaGrade);
      const res = await axios.post(`${API}/api/pcusa-export/bundle?${params.toString()}`, {}, {
        responseType: 'blob',
        withCredentials: true,
        timeout: 300000,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mosaic-pcusa-bundle-${pcusaTier}${pcusaGrade ? '-G' + pcusaGrade : ''}-qty${qty}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PCUSA bundle ready — unzip, upload interior.pdf + cover.pdf per folder to printingcenterusa.com');
    } catch (e) {
      let detail = 'PCUSA bundle build failed';
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          detail = j.detail || txt.slice(0, 200);
        } catch { /* fall through */ }
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(detail);
    }
    setPcusaBusy(false);
  };

  const downloadKCardsPdf = async () => {
    setPcusaBusy(true);
    toast.info('Building K Screener Picture Card Set PDF…');
    try {
      const res = await axios.get(`${API}/api/pcusa-export/k-picture-cards.pdf`, {
        responseType: 'blob',
        withCredentials: true,
        timeout: 120000,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'MOSAIC_K_Screener_PictureCards.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('K Picture Card Set downloaded — print on 100# cardstock, cut on dashed lines');
    } catch (e) {
      let detail = 'K card set download failed';
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          detail = j.detail || txt.slice(0, 200);
        } catch { /* fall through */ }
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(detail);
    }
    setPcusaBusy(false);
  };

  const regen = async (qid) => {
    setRegenId(qid);
    try {
      await axios.post(`${API}/api/entering-level/regenerate`, { question_id: qid }, { withCredentials: true });
      toast.success('Regenerated');
      await loadPreview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Regen failed'); }
    setRegenId(null);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="entering-studio">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="es-title">
          MOSAIC — Entering-Level Studio
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Simplified K-2 stems for WIDA Entering-Level (Level 1-2) learners. This SKU is fully additive —
          the standard question text is never mutated, so the standard print-digital parity lock is intact.
          A separate Entering-Level BLM PDF is generated on demand via the existing BLM pipeline
          (<code className="bg-slate-200 px-1 rounded">tier=ENTERING</code>).
        </p>

        {stats && (
          <div className="mt-6 bg-white border border-slate-200 rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-800">Coverage</div>
              <div className="text-sm text-slate-600" data-testid="es-overall">
                <span className="text-emerald-700 font-bold">{stats.overall.pct}%</span> ·
                {' '}{stats.overall.done}/{stats.overall.total} K-2 stems simplified
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {stats.cells.map(c => (
                <div key={`${c.grade}-${c.modality}`} className="border border-slate-200 rounded p-2 text-xs" data-testid={`es-cell-${c.grade}-${c.modality}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">G{c.grade} · {c.modality}</span>
                    <Badge className={c.pct >= 100 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                      {c.pct}%
                    </Badge>
                  </div>
                  <div className="text-slate-500 mt-1">
                    {c.done}/{c.total} · L1-2 ready: <b>{c.level12_ready}</b>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Teacher-Edition Parity panel */}
        {parity && (
          <div className="mt-4 bg-white border border-slate-200 rounded p-4" data-testid="parity-panel">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 text-blue-700 flex items-center justify-center rounded flex-shrink-0">
                  <ShieldCheck size={20}/>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800">Teacher-Edition Parity (K-2)</div>
                  <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                    Every teacher edition reads from the same DB rows the student edition uses — so image regens and Entering-Level stem swaps propagate automatically. The panel below tracks which cached PDFs are already up-to-date and which are cold-cache (will build on first request).
                  </p>
                  <div className="mt-2 flex gap-3 text-xs">
                    <Badge className="bg-emerald-100 text-emerald-800" data-testid="parity-current">
                      Current: {parity.summary.current}
                    </Badge>
                    <Badge className={parity.summary.stale === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'} data-testid="parity-stale">
                      Stale: {parity.summary.stale}
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-700" data-testid="parity-missing">
                      Cold cache: {parity.summary.missing}
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-700">
                      Total slots: {parity.summary.total}
                    </Badge>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => prewarm('entering')}
                disabled={prewarming !== null}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="prewarm-entering-btn"
              >
                {prewarming === 'entering' ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Flame className="mr-2 h-4 w-4"/>}
                Pre-warm 24 Entering PDFs
              </Button>
            </div>
            <div className="mt-3 flex gap-2">
              <Button
                variant="outline"
                onClick={() => prewarm('standard')}
                disabled={prewarming !== null}
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                data-testid="prewarm-standard-btn"
              >
                {prewarming === 'standard' ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Flame className="mr-2 h-4 w-4"/>}
                Pre-warm 72 Standard K-2 PDFs (A / BC / ALL × student + teacher)
              </Button>
            </div>
          </div>
        )}

        {/* Entering-Level Teacher Explainer (what & when to use) */}
        <div className="mt-4 bg-white border border-slate-200 rounded p-4" data-testid="entering-explainer">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-100 text-emerald-700 flex items-center justify-center rounded flex-shrink-0">
              <HelpCircle size={20}/>
            </div>
            <div className="text-sm text-slate-700">
              <div className="font-semibold text-slate-900 mb-1">
                When to use the Entering-Level tier
              </div>
              <p className="mb-2">
                The <b>Entering-Level (WIDA Level 1-2)</b> tier is for K-2 learners who are new to English and
                cannot yet decode the standard question stem length. It preserves everything visual and
                pedagogically anchored — the scene image, all four answer options, and the correct answer are
                identical to the standard booklet — and rewrites only the question stem in 4-8 word Tier-1
                vocabulary with a simple <i>What/Who/Where/Which is __?</i> form.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <div className="border border-emerald-200 bg-emerald-50 rounded p-3">
                  <div className="text-xs font-bold text-emerald-800 mb-1 uppercase">Assign Entering-Level when…</div>
                  <ul className="list-disc list-inside space-y-1 text-xs text-slate-700">
                    <li>Learner is new-to-English (in-country ≤ 6 months) or a Level 1-2 on the WIDA screener</li>
                    <li>Learner recognizes basic Tier-1 vocab (kid, dog, home) but cannot decode multi-clause stems</li>
                    <li>Learner is emergent-literate in home language but pre-emergent in English</li>
                    <li>Teacher wants a fair check-in without inflating <i>reading load</i> as a construct</li>
                  </ul>
                </div>
                <div className="border border-slate-200 bg-slate-50 rounded p-3">
                  <div className="text-xs font-bold text-slate-700 mb-1 uppercase">Stay on Standard when…</div>
                  <ul className="list-disc list-inside space-y-1 text-xs text-slate-700">
                    <li>Learner is at WIDA Level 3+ (Developing) or above</li>
                    <li>Learner has ≥ 12 months in an English-medium classroom</li>
                    <li>You need a longitudinal baseline that matches prior test administrations</li>
                    <li>You are preparing for the standard WIDA ACCESS window</li>
                  </ul>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                <b>Fair-use rule:</b> Entering-Level is a diagnostic accommodation, not a placement decision.
                Combine with the MOSAIC WIDA Screener result and the teacher's classroom observation before
                deciding a learner's ELL tier.
              </p>
            </div>
          </div>
        </div>

        {/* KDP Export panel */}
        <div className="mt-4 bg-white border border-slate-200 rounded p-4" data-testid="kdp-export-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-amber-100 text-amber-700 flex items-center justify-center rounded flex-shrink-0">
                <Package size={20}/>
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800">Amazon KDP Print-On-Demand Bundle</div>
                <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                  Package cached BLM PDFs into a ZIP ready to drop into Amazon Kindle Direct Publishing.
                  Each folder inside the ZIP contains <code className="bg-slate-100 px-1 rounded">interior.pdf</code>,
                  a freshly generated wraparound <code className="bg-slate-100 px-1 rounded">cover.pdf</code>,
                  <code className="bg-slate-100 px-1 rounded"> metadata.json</code>, and a printable upload
                  checklist with royalty math baked in. Sign in at <b>kdp.amazon.com</b> → <b>+ Create → Paperback</b>,
                  then follow the README inside the ZIP.
                </p>
                <p className="text-[11px] text-amber-800 mt-2 max-w-2xl bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  <b>Note:</b> Amazon KDP requires the book to be publicly listed for retail. For district
                  procurement (where public retail pricing conflicts with negotiated contract pricing) use
                  the <b>PrintingCenterUSA</b> panel below — same PDFs, no retail listing required.
                </p>
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  <label className="text-xs font-medium text-slate-700">Tier</label>
                  <Select value={kdpTier} onValueChange={setKdpTier}>
                    <SelectTrigger className="w-40 h-8 text-xs" data-testid="kdp-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ENTERING" data-testid="kdp-tier-ENTERING">Entering-Level</SelectItem>
                      <SelectItem value="A" data-testid="kdp-tier-A">Standard Tier A</SelectItem>
                      <SelectItem value="BC" data-testid="kdp-tier-BC">Standard Tier BC</SelectItem>
                      <SelectItem value="ALL" data-testid="kdp-tier-ALL">Standard Tier ALL</SelectItem>
                      <SelectItem value="SCREENER" data-testid="kdp-tier-SCREENER">Screener</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="text-xs font-medium text-slate-700 ml-2">Grade (optional)</label>
                  <Select value={kdpGrade || 'all'} onValueChange={(v) => setKdpGrade(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid="kdp-grade-select"><SelectValue placeholder="All"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="kdp-grade-all">All grades</SelectItem>
                      <SelectItem value="K" data-testid="kdp-grade-K">K</SelectItem>
                      <SelectItem value="1" data-testid="kdp-grade-1">1</SelectItem>
                      <SelectItem value="2" data-testid="kdp-grade-2">2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <Button
              onClick={downloadKdpBundle}
              disabled={kdpBusy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="kdp-download-btn"
            >
              {kdpBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Package className="mr-2 h-4 w-4"/>}
              Download KDP Bundle
            </Button>
          </div>
          <div className="mt-3 pl-13">
            <Button
              variant="outline"
              onClick={downloadKdpBinder}
              disabled={kdpBusy}
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="kdp-binder-btn"
            >
              {kdpBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <FileText className="mr-2 h-4 w-4"/>}
              Download Desk-Binder PDF (clickable + printable, one page per booklet)
            </Button>
          </div>
        </div>

        {/* PCUSA Export panel */}
        <div className="mt-4 bg-white border border-emerald-200 rounded p-4" data-testid="pcusa-export-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-emerald-100 text-emerald-700 flex items-center justify-center rounded flex-shrink-0">
                <FileText size={20}/>
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800">
                  PrintingCenterUSA — District-Safe Order Sheets
                  <Badge className="ml-2 bg-emerald-100 text-emerald-800">B2B Print</Badge>
                </div>
                <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                  <b>Upload Bundle (ZIP)</b> — one folder per booklet containing the actual
                  <code className="bg-slate-100 px-1 rounded"> interior.pdf</code> +
                  <code className="bg-slate-100 px-1 rounded"> cover.pdf</code> + a pre-filled
                  <code className="bg-slate-100 px-1 rounded"> PCUSA_specs.txt</code>. Upload the two
                  PDFs directly to <b>printingcenterusa.com/printing/book-printing</b>. Interior is spec'd
                  as <b>60# uncoated offset (writable — pencil/pen safe)</b>; cover uses gloss laminate
                  for durability. Binding auto-selects Saddle Stitch ≤ 92 pp / Perfect Bound above.
                  <b> No public retail listing required</b> — preserves district contract pricing.
                </p>
                <p className="text-[11px] text-emerald-800 mt-2 max-w-2xl bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                  <b>K Screener bundles auto-include the Picture Card Set</b> (16 cards on cardstock —
                  required for K-L1 / K-L2 / K-S1 / K-S3 / K-R1 / K-R2 / K-W2 items). Sold separately
                  as SKU <code className="bg-white px-1 rounded">MOS-SCR-CARDS-K</code> @ $29.99.
                </p>
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  <label className="text-xs font-medium text-slate-700">Tier</label>
                  <Select value={pcusaTier} onValueChange={setPcusaTier}>
                    <SelectTrigger className="w-40 h-8 text-xs" data-testid="pcusa-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ENTERING" data-testid="pcusa-tier-ENTERING">Entering-Level</SelectItem>
                      <SelectItem value="A" data-testid="pcusa-tier-A">Standard Tier A</SelectItem>
                      <SelectItem value="BC" data-testid="pcusa-tier-BC">Standard Tier BC</SelectItem>
                      <SelectItem value="ALL" data-testid="pcusa-tier-ALL">Standard Tier ALL</SelectItem>
                      <SelectItem value="SCREENER" data-testid="pcusa-tier-SCREENER">Screener</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="text-xs font-medium text-slate-700 ml-2">Grade (optional)</label>
                  <Select value={pcusaGrade || 'all'} onValueChange={(v) => setPcusaGrade(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid="pcusa-grade-select"><SelectValue placeholder="All"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="pcusa-grade-all">All grades</SelectItem>
                      <SelectItem value="K" data-testid="pcusa-grade-K">K</SelectItem>
                      <SelectItem value="1" data-testid="pcusa-grade-1">1</SelectItem>
                      <SelectItem value="2" data-testid="pcusa-grade-2">2</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="text-xs font-medium text-slate-700 ml-2">Qty per booklet</label>
                  <Select value={pcusaQty} onValueChange={setPcusaQty}>
                    <SelectTrigger className="w-28 h-8 text-xs" data-testid="pcusa-qty-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10" data-testid="pcusa-qty-10">10 (MOQ)</SelectItem>
                      <SelectItem value="25" data-testid="pcusa-qty-25">25</SelectItem>
                      <SelectItem value="50" data-testid="pcusa-qty-50">50</SelectItem>
                      <SelectItem value="100" data-testid="pcusa-qty-100">100</SelectItem>
                      <SelectItem value="250" data-testid="pcusa-qty-250">250</SelectItem>
                      <SelectItem value="500" data-testid="pcusa-qty-500">500</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={downloadPcusaBundle}
                disabled={pcusaBusy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="pcusa-bundle-btn"
              >
                {pcusaBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Package className="mr-2 h-4 w-4"/>}
                Download PCUSA Upload Bundle (ZIP)
              </Button>
              <Button
                onClick={downloadPcusaOrderSheets}
                disabled={pcusaBusy}
                variant="outline"
                className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                data-testid="pcusa-download-btn"
              >
                {pcusaBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <FileText className="mr-2 h-4 w-4"/>}
                Order Sheets Only (PDF)
              </Button>
              <Button
                onClick={downloadKCardsPdf}
                disabled={pcusaBusy}
                variant="outline"
                className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                data-testid="pcusa-k-cards-btn"
              >
                {pcusaBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <FileText className="mr-2 h-4 w-4"/>}
                K Picture Cards (PDF)
              </Button>
            </div>
          </div>
        </div>



        {/* Screener Parity panel */}
        {screenerParity && (
          <div className="mt-4 bg-white border border-slate-200 rounded p-4" data-testid="screener-parity-panel">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-indigo-100 text-indigo-700 flex items-center justify-center rounded flex-shrink-0">
                  <Layers size={20}/>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800">MOSAIC WIDA Screener — BLM Parity</div>
                  <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                    Screener content is a 1-for-1 mirror of the digital screener (source: <code className="bg-slate-200 px-1 rounded">services/mosaic_screener.SCREENER_ITEMS</code>).
                    Vision Verifier: <b>N/A</b> — screener items are text/audio prompts with zero rendered scene images. 10 print slots total: 5 grade bands × student + teacher.
                  </p>
                  <div className="mt-2 flex gap-3 text-xs">
                    <Badge className="bg-emerald-100 text-emerald-800" data-testid="screener-parity-current">
                      Current: {screenerParity.summary.current}
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-700" data-testid="screener-parity-missing">
                      Cold cache: {screenerParity.summary.missing}
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-700">Total slots: {screenerParity.summary.total}</Badge>
                    <Badge className="bg-blue-100 text-blue-800">Vision check: N/A (no images)</Badge>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => prewarm('screener')}
                disabled={prewarming !== null}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="prewarm-screener-btn"
              >
                {prewarming === 'screener' ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Flame className="mr-2 h-4 w-4"/>}
                Pre-warm 10 Screener PDFs
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center gap-3 bg-white border border-slate-200 rounded p-4">
          <label className="text-sm font-medium">Grade</label>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger className="w-24" data-testid="es-grade"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['K', '1', '2'].map(g => <SelectItem key={g} value={g} data-testid={`es-grade-${g}`}>Grade {g}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="text-sm font-medium ml-3">Modality</label>
          <Select value={modality} onValueChange={setModality}>
            <SelectTrigger className="w-36" data-testid="es-modality"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['listening', 'reading', 'writing', 'speaking'].map(m => (
                <SelectItem key={m} value={m} data-testid={`es-modality-${m}`}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-slate-500">
            {loading ? 'Loading…' : `${preview.length} L1-2 items shown`}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400"/></div>
        ) : (
          <div className="mt-4 space-y-3">
            {preview.map((q, idx) => (
              <div key={q.id} className="bg-white border border-slate-200 rounded p-4" data-testid={`es-row-${idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase text-slate-500 tracking-wide">
                    #{q.question_number} · WIDA L{q.wida_level} · <span className="font-mono">{q.id.slice(0,10)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-slate-100 text-slate-700">
                      {q.standard_word_count}w → {q.simplified_word_count}w
                      ({q.delta_words > 0 ? '−' : '+'}{Math.abs(q.delta_words)})
                    </Badge>
                    <Button
                      variant="ghost" size="sm"
                      disabled={regenId === q.id}
                      onClick={() => regen(q.id)}
                      data-testid={`es-regen-${idx}`}
                    >
                      {regenId === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-slate-200 rounded p-3 bg-slate-50">
                    <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Standard</div>
                    <div className="text-sm text-slate-800" data-testid={`es-standard-${idx}`}>{q.standard_stem}</div>
                  </div>
                  <div className="border border-emerald-300 rounded p-3 bg-emerald-50">
                    <div className="text-[10px] uppercase font-semibold text-emerald-700 mb-1 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3"/> Entering-Level
                    </div>
                    <div className="text-sm text-emerald-900 font-medium" data-testid={`es-simplified-${idx}`}>{q.simplified_stem}</div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                  <ArrowRight className="h-3 w-3"/>
                  Correct: <b className="text-slate-700">{q.correct_answer}</b>
                  <span className="ml-2">· options: {q.options.join(' | ')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
